<template>
  <div class="catalog small-scroll-bar small-scroll-bar--black">
    <div class="catalog-container">
      <olympiad-banner class="olympiad-banner" />
      <olympiad-blocks />
    </div>

    <div class="catalog_inner">
      <h1 class="catalog-container catalog__title">
        {{ titleText }}
      </h1>

      <div class="catalog-container catalog__filter">
        <form-select-field
          v-model="currentSubject"
          :items="materialSubjects"
          drop-down-image="/img/materials/olympiad/drop-down-arrow.svg"
          placeholder="все предметы"
          @update="setPathWithFilters"
        />

        <form-select-field
          v-model="currentAge"
          :items="materialAges"
          drop-down-image="/img/materials/olympiad/drop-down-arrow.svg"
          placeholder="все классы"
          @update="setPathWithFilters"
        />
      </div>

      <div class="catalog-container materials-list">
        <h3>сейчас идут</h3>

        <nuxt-link
          v-for="material in filteredOlympiads.underway"
          :key="material.id"
          :to="materialRoute(material)"
          class="materials-item"
        >
          <img
            :src="materialImage(material)"
            :alt="material.title"
            loading="lazy"
            @error="imageLoadError(material)"
            @dragstart.prevent
          >
        </nuxt-link>

        <div
          v-if="filteredOlympiads.allUnderway.length > 4 && !showAll.underway"
          class="materials-list__show-more"
        >
          <button
            class="m-btn"
            @click="showAll.underway = true"
          >
            показать еще олимпиады
          </button>
        </div>
      </div>
      <div class="catalog-container materials-list">
        <h3>скоро начало</h3>

        <nuxt-link
          v-for="material in filteredOlympiads.soon"
          :key="material.id"
          :to="materialRoute(material)"
          class="materials-item"
        >
          <img
            :src="materialImage(material)"
            :alt="material.title"
            loading="lazy"
            @error="imageLoadError(material)"
            @dragstart.prevent
          >
        </nuxt-link>

        <div
          v-if="filteredOlympiads.allSoon.length > 4 && !showAll.soon"
          class="materials-list__show-more"
        >
          <button
            class="m-btn"
            @click="showAll.soon = true"
          >
            показать еще олимпиады
          </button>
        </div>
      </div>
      <div class="catalog-container materials-list">
        <h3>завершились</h3>

        <nuxt-link
          v-for="material in filteredOlympiads.complete"
          :key="material.id"
          :to="materialRoute(material)"
          class="materials-item"
        >
          <img
            :src="materialImage(material)"
            :alt="material.title"
            loading="lazy"
            @error="imageLoadError(material)"
            @dragstart.prevent
          >
        </nuxt-link>

        <div
          v-if="filteredOlympiads.allComplete.length > 4 && !showAll.complete"
          class="materials-list__show-more"
        >
          <button
            class="m-btn"
            @click="showAll.complete = true"
          >
            показать еще олимпиады
          </button>
        </div>
      </div>
    </div>

    <materials-subscription
      class="catalog-container"
    />

    <school-new-breadcrumbs
      class="catalog-container"
      :breadcrumbs="breadcrumbs"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import HeadMixin from '~/mixins/HeadMixin';

import SchoolNewBreadcrumbs from '~/components/v2/school-new/SchoolNewBreadcrumbs.vue';
import { getCategoryImageUrl, getCategoryShareImage } from '../utils';
import MaterialsSubscription from '../MaterialsSubscription.vue';
import OlympiadBanner from './OlympiadBanner.vue';
import OlympiadBlocks from './OlympiadBlocks.vue';
import FormSelectField from '../../v2/common/fileds/FormSelectField.vue';

export default {
  name: 'OlympiadMaterials',
  components: {
    FormSelectField,
    OlympiadBlocks,
    OlympiadBanner,
    MaterialsSubscription,
    SchoolNewBreadcrumbs,
  },
  mixins: [HeadMixin],

  layout: 'DefaultLayoutV2',

  data: () => ({
    olympiadPacks: [],
    materials: [],

    currentSubject: 'all',
    currentAge: 'all',

    showAll: {
      soon: false,
      underway: false,
      complete: false,
    },
  }),

  computed: {
    ...mapGetters({
      categories: 'getMaterialCategories',
    }),
    breadcrumbs() {
      return [
        { text: 'главная', to: '/' },
        { text: 'олимпиады', to: this.$route.path },
      ];
    },
    routeCategory() {
      return this.$route.params.material_category || 'olympiad';
    },
    currentCategory() {
      return this.categories.find(({ slug }) => this.routeCategory === slug) || {};
    },

    titleText() {
      let title = 'онлайн-олимпиады';

      if (this.currentSubject !== 'all') {
        const subject = this.materialSubjects.find((item) => this.currentSubject === item.value);
        title += ` по предмету ${subject.title}`;
      }

      if (this.currentAge !== 'all') {
        const age = this.materialAges.find((item) => this.currentAge === item.value);
        const ageTitle = age.title
          .replace(/классы$/, 'классов')
          .replace(/класс$/, 'класса');
        title += ` для ${ageTitle}`;
      }

      return title;
    },

    materialAges() {
      let ages = [];

      if (this.materials.length && this.olympiadPacks.length) {
        this.materials.forEach((material) => {
          const olympiadPack = this.getOlympiadPack(material);
          if (!olympiadPack) return;

          const olympiad = this.getOlympiad(material);
          if (!olympiad) return;

          const haveThisVariant = ages.find(({ value }) => value === olympiad.ageGroup);
          if (haveThisVariant) return;

          ages.push({
            value: olympiad.ageGroup,
            title: olympiad.tAgeGroup,
          });
        });

        ages = ages.filter((age) => {
          if (age.value.includes('year')) return false;

          const grade = parseInt(age.title, 10);
          if (grade < 4) return false;

          return true;
        });

        ages.sort((a, b) => (
          parseInt(a.title, 10) - parseInt(b.title, 10)
        ));
      }

      return [
        { value: 'all', title: 'все классы' },
        ...ages,
      ];
    },

    materialSubjects() {
      const subjects = [];

      if (this.materials.length && this.olympiadPacks.length) {
        this.materials.forEach((material) => {
          const olympiadPack = this.getOlympiadPack(material);
          if (!olympiadPack?.subject?.id) return;

          const haveThisVariant = subjects.find(({ value }) => value === olympiadPack.subject.id);
          if (haveThisVariant) return;

          subjects.push({
            value: olympiadPack.subject.id,
            title: olympiadPack.subject.name,
          });
        });

        subjects.sort((a, b) => (a.title.localeCompare(b.title)));
      }

      return [
        { value: 'all', title: 'все предметы' },
        ...subjects,
      ];
    },

    filteredOlympiads() {
      const now = new Date();
      const soon = [];
      const underway = [];
      const complete = [];

      if (this.materials.length && this.olympiadPacks.length) {
        this.materials.forEach((item) => {
          const olympiadPack = this.getOlympiadPack(item);
          if (!olympiadPack) return;

          const olympiad = this.getOlympiad(item);
          if (!olympiad) return;

          if (this.currentAge !== 'all' && this.currentAge !== olympiad.ageGroup) return;

          if (this.currentSubject !== 'all' && this.currentSubject !== olympiadPack.subject?.id) return;

          const material = { ...item, olympiadPack, olympiad };

          if (+now > +new Date(olympiadPack.endsAt)) {
            complete.push(material);
          } else if (+now > +new Date(olympiadPack.startsAt)) {
            underway.push(material);
          } else {
            soon.push(material);
          }
        });
      }

      return {
        allSoon: soon,
        soon: this.showAll.soon ? soon : soon.slice(0, 4),
        allUnderway: underway,
        underway: this.showAll.underway ? underway : underway.slice(0, 4),
        allComplete: complete,
        complete: this.showAll.complete ? complete : complete.slice(0, 4),
      };
    },

    // для head
    title() {
      // eslint-disable-next-line
      return this.currentCategory?.title || 'Медиа для родителей и детей: образование, школа, ЕГЭ, ОГЭ, профессии, психология';
    },
    description() {
      // eslint-disable-next-line
      return this.currentCategory?.description || 'В нашем блоге найдете актуальные новости, советы по домашнему обучению, статьи о психологии ребенка, лайфхаки для успешной учебы, информацию о профессиях, а также полезные советы для родителей';
    },
    image() {
      return getCategoryShareImage(this.currentCategory);
    },
  },

  watch: {
    // eslint-disable-next-line func-names
    'materialSubjects.length': 'setFilterByRoute',
    'materialAges.length': 'setFilterByRoute',
  },

  async created() {
    await Promise.all([
      this.fetchOlympiads(),
      this.fetchKidsOlympiadPacks(),
    ]);
    this.setFilterByRoute();
  },

  methods: {
    async fetchOlympiads() {
      const params = {
        page: 1,
        perPage: 1000,
        orderAttributes: { id: 'desc' },
        kind: 'article',
        categoriesIds: [Number(this.currentCategory.id)],
      };

      const { publicMaterials } = await this.$api.getMaterials(params);
      this.materials = publicMaterials.map((material) => ({
        ...material,
        customAttributes: JSON.parse(material.customAttributes || '{}'),
      }));
    },
    async fetchKidsOlympiadPacks() {
      this.olympiadPacks = await this.$kidsApi.getPublishedOlympiadPacks();
    },
    materialRoute(material) {
      return `/${this.routeCategory}/${material.slug || material.id}`;
    },
    materialImage(material) {
      if (material.covers.length) {
        return material.covers[0].url;
      }

      return [getCategoryImageUrl(this.routeCategory)];
    },
    imageLoadError(material) {
      if (material.covers[0]) {
        // eslint-disable-next-line
        material.covers[0].url = getCategoryImageUrl(this.routeCategory);
      }
    },

    getOlympiadPack({ customAttributes }) {
      if (!customAttributes) return null;

      const olympiadPack = this.olympiadPacks.find(({ id }) => +id === +customAttributes.olympiadPackId);

      return olympiadPack;
    },
    getOlympiad({ customAttributes }) {
      if (!customAttributes) return null;

      const olympiadPack = this.getOlympiadPack({ customAttributes });
      if (!olympiadPack) return null;

      const olympiad = olympiadPack?.olympiads?.find(({ id }) => +id === +customAttributes.olympiadId);

      return olympiad;
    },

    setFilterByRoute() {
      const { first, second } = this.$route.params;

      if (first) {
        if (this.materialSubjects.some(({ value }) => value === first)) this.currentSubject = first;
        if (this.materialAges.some(({ value }) => value === first)) this.currentAge = first;
      }
      if (second) {
        if (this.materialAges.some(({ value }) => value === second)) this.currentAge = second;
      }
    },
    setPathWithFilters() {
      let path = '';
      if (this.currentSubject !== 'all') path += `/${this.currentSubject}`;
      if (this.currentAge !== 'all') path += `/${this.currentAge}`;

      if (path) {
        this.$router.push({ path: `/olympiad/filter${path}`, query: this.$route.query });
        return;
      }

      this.$router.push({ path: '/olympiad', query: this.$route.query });
    },
  },
};
</script>

<style lang="scss" scoped>

.catalog {
  padding-top: calc(var(--scale) * 120);
  padding-bottom: calc(var(--scale) * 58);
  background-color: #ECEDF0;
  overflow: hidden;

  @include media-down($size-tablet) {
    padding-top: calc(var(--scale) * 104);
    padding-bottom: calc(var(--scale) * 24);
  }

  ::v-deep(*) {
    font-family: "Onest", sans-serif !important;
  }

  .catalog-container {
    margin-inline: calc(var(--scale) * 40);

    @include media-down($size-tablet) {
      margin-inline: calc(var(--scale) * 16);
    }

    @include media-down($size-mobile) {
      margin-inline: calc(var(--scale) * 12);
    }
  }

  .catalog_inner {
    width: 100%;
    margin-top: calc(var(--scale) * 60);
    margin-bottom: calc(var(--scale) * 38);
    padding-block: calc(var(--scale) * 60);
    background-color: #FFFFFF;
    border-radius: calc(var(--scale) * 60);

    @include media-down($size-tablet) {
      margin-bottom: calc(var(--scale) * 40);
      padding-block: calc(var(--scale) * 40);
      border-radius: calc(var(--scale) * 20);
    }

    @include media-down($size-mobile) {
      margin-block: calc(var(--scale) * 64);
      padding-block: calc(var(--scale) * 16);
    }
  }

  &__title {
    margin-bottom: calc(var(--scale) * 32);
    font-weight: 500;
    font-size: calc(var(--scale) * 64);
    line-height: 95%;
    letter-spacing: -0.02em;
    color: #02120F;
    text-transform: lowercase;
  }

  &__filter {
    margin-bottom: calc(var(--scale) * 80);
    display: flex;
    align-items: center;
    gap: calc(var(--scale) * 8);

    @include media-down($size-mobile) {
      margin-inline: 0 !important;
      padding-inline: calc(var(--scale) * 12);
      gap: calc(var(--scale) * 4);
    }

    ::v-deep(.select-field) {
      min-width: calc(var(--scale) * 445);

      @include media-down($size-tablet) {
        min-width: calc(var(--scale) * 228);
      }

      @include media-down($size-mobile) {
        min-width: auto;
        width: calc(50% - (var(--scale) * 2));
      }

      .select_wrapper {
        padding: calc(var(--scale) * 32) calc(var(--scale) * 54);
        border: 1px solid #A7ABAC;
        border-radius: 100px;

        @include media-down($size-tablet) {
          padding: calc(var(--scale) * 16) calc(var(--scale) * 20);
        }
      }

      .select_inner .select_result {
        font-weight: 500;
        font-size: calc(var(--scale) * 32);
        line-height: 110%;
        color: #02120F;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        @include media-down($size-tablet) {
          font-size: calc(var(--scale) * 18);
        }
      }

      .select_dropdown {
        padding-block: calc(var(--scale) * 12);
        top: calc(var(--scale) * 94);
        width: 100%;
        max-height: calc(var(--scale) * 500);
        overflow: auto;
        border: 1px solid #A7ABAC;
        border-radius: calc(var(--scale) * 32);

        @include media-down($size-tablet) {
          padding-block: calc(var(--scale) * 8);
          top: calc(var(--scale) * 54);
          border-radius: calc(var(--scale) * 16);
          max-height: calc(var(--scale) * 300);
        }

        .select_item {
          padding: calc(var(--scale) * 10) calc(var(--scale) * 20);
          font-size: calc(var(--scale) * 24);
          font-weight: 500;
          line-height: 100%;
          text-align: left;

          @include media-down($size-tablet) {
            padding: calc(var(--scale) * 6) calc(var(--scale) * 10);
            font-size: calc(var(--scale) * 14);
          }

          &--selected {
            color: #FA5019;
          }
        }
      }

      .select_arrow {
        transition: 200ms;
        width: calc(var(--scale) * 19);

        @include media-down($size-tablet) {
          width: calc(var(--scale) * 10);
        }
      }
    }
  }

  .materials-subscription {
    margin-bottom: calc(var(--scale) * 128);

    @include media-down($size-tablet) {
      margin-bottom: calc(var(--scale) * 80);
    }
  }

  .olympiad-banner {
    margin-bottom: calc(var(--scale) * 20);

    @include media-down($size-tablet) {
      margin-bottom: calc(var(--scale) * 16);
    }
  }
}

.materials-list {
  display: flex;
  flex-wrap: wrap;
  gap: calc(var(--scale) * 40) calc(var(--scale) * 20);

  &:not(:last-child) {
    margin-bottom: calc(var(--scale) * 80);
  }

  @include media-down($size-tablet) {
    gap: calc(var(--scale) * 20) calc(var(--scale) * 8);
  }

  h3 {
    width: 100%;
    font-weight: 500;
    font-size: calc(var(--scale) * 64);
    line-height: 95%;
    letter-spacing: -0.02em;
    color: #02120F;

    @include media-down($size-tablet) {
      margin-bottom: calc(var(--scale) * 4);
      font-size: calc(var(--scale) * 44);
      line-height: 90%;
      letter-spacing: -0.06em;
    }
  }

  .materials-item {
    width: calc((100% / 4) - (calc(var(--scale) * 60) / 4));
    aspect-ratio: 1/1;
    border-radius: calc(var(--scale) * 40);
    overflow: hidden;

    @include media-down($size-tablet) {
      width: calc((100% / 2) - var(--scale) * 4);
    }

    @include media-down($size-mobile) {
      width: 100%;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__show-more {
    width: 100%;
    display: flex;
    justify-content: center;

    button {
      padding: calc(var(--scale) * 44) calc(var(--scale) * 56);
      border: 1px solid #A7ABAC;
      border-radius: calc(var(--scale) * 40);

      font-weight: 500;
      font-size: calc(var(--scale) * 32);
      line-height: 110%;
      color: #02120F;

      @include media-down($size-tablet) {
        width: 100%;
        padding: calc(var(--scale) * 30) 0;
        border-radius: calc(var(--scale) * 24);
        font-size: calc(var(--scale) * 18);
      }
    }
  }
}

// артефакты от старых страниц
::v-deep section {
  margin-bottom: 0;

  h2,
  h3 {
    margin: 0;
  }
}

</style>
